import React, { type FC } from 'react';
import { expValEquals } from '@confluence/feature-experiments';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import Tooltip from '@atlaskit/tooltip';
import type { AvatarProps } from '@atlaskit/avatar-group';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import type { BackgroundColor, TextColor } from '@atlaskit/primitives';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, Box, xcss } from '@atlaskit/primitives';

import type { Participant, PresenceContentMode, PresenceContentType } from '../presenceTypes.ts';
import {
	getCurrentUserColors,
	getParticipantColors,
	isCanvasContentType,
} from '../presenceUtils.ts';

const i18n = defineMessages({
	editing: {
		id: 'team-presence.menu.editing',
		defaultMessage: 'Editing',
		description:
			'The text is shown as a title of a section inside of a menu. The people in the section are editing a page',
	},
	viewing: {
		id: 'team-presence.menu.viewing',
		defaultMessage: 'Viewing',
		description: 'The text is used to indicate that the current user is `Viewing the page`',
	},
	clickToFind: {
		id: 'team-presence.menu.click-to-find',
		defaultMessage: 'Click to find',
		description:
			"The text is used in the avatar tooltip to indicate that the avatar can be clicked to locate the user's pointer in the document. When clicked, the editor scrolls to that pointer's position.",
	},
	clickToFindNotAvailable: {
		id: 'team-presence.menu.click-to-find-not-available',
		defaultMessage: 'Inactive',
		description:
			"If for some reason (e.g., user not focusing to document) we cannot locate the telepointer, this message shows up instead of 'clickToFind' message.",
	},
	clickToFollow: {
		id: 'team-presence.menu.click-to-follow',
		defaultMessage: 'Click to follow',
		description:
			"The text is used in the presence menu item to indicate that the menu item can be clicked to follow the user's pointer",
	},
});

const containerStyles = xcss({
	position: 'relative',
});

const avatarBadgeStyles = xcss({
	position: 'absolute',
	bottom: '-2px',
	right: '-2px',
	height: '12px',
	width: '12px',
	borderRadius: 'radius.small',
	color: 'color.text.inverse', // TODO: This will need to be swapped for the foregroundColor, based on sessionId
	boxShadow: 'elevation.shadow.overflow',
	fontWeight: 'font.weight.semibold',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '10px',
	zIndex: 'card',
	userSelect: 'none',
});

const tooltipTextStyles = xcss({
	textAlign: 'center',
});

type EditorBadgeProps = {
	name: string;
	badgeTextColor: string;
} & Pick<PresenceAvatarProps, 'borderColor'>;

const EditorAvatarBadge: FC<EditorBadgeProps> = ({
	borderColor: backgroundColor,
	badgeTextColor,
	name,
}) => {
	const firstInitial = name?.substring(0, 1);

	/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values,@atlaskit/design-system/consistent-css-prop-usage,@atlaskit/ui-styling-standard/no-unsafe-values */
	return (
		<Flex
			testId="editor-avatar-badge"
			alignItems="center"
			justifyContent="center"
			xcss={[
				avatarBadgeStyles,
				xcss({
					backgroundColor: backgroundColor as BackgroundColor,
					color: badgeTextColor as TextColor,
				}),
			]}
		>
			<Box testId="editor-avatar-badge-initial" as="span">
				{firstInitial}
			</Box>
		</Flex>
	);
	/* eslint-enable @atlaskit/ui-styling-standard/no-imported-style-values,@atlaskit/design-system/consistent-css-prop-usage,@atlaskit/ui-styling-standard/no-unsafe-values */
};

const TooltipText = ({ children }: { children: React.ReactNode }) => (
	<Box xcss={tooltipTextStyles}>{children}</Box>
);

export type PresenceAvatarProps = Omit<AvatarProps, 'name'> & {
	contentId: string;
	contentMode: PresenceContentMode;
	contentType: PresenceContentType;
	isEligibleToScroll?: boolean;
	isScrollToFeatureEnabled?: boolean;
	participant: Participant;
};

export const PresenceAvatar = ({
	contentMode: _contentMode,
	contentId: _contentId,
	contentType,
	isEligibleToScroll,
	isScrollToFeatureEnabled,
	participant,
	...avatarProps
}: PresenceAvatarProps) => {
	const { formatMessage } = useIntl();
	const { activity, color: participantColor, id, isCurrentUser, name, src } = participant;

	const displayedSrc =
		isCurrentUser &&
		expValEquals('team_presence_avatar_src_stabilization_ab', 'cohort', 'treatment')
			? `/wiki/aa-avatar/${id}`
			: src;

	const activityText =
		activity === 'editor' ? formatMessage(i18n.editing) : formatMessage(i18n.viewing);
	const label = isCanvasContentType(contentType) ? name : `${name} (${activityText})`;

	const colors = isCurrentUser ? getCurrentUserColors() : getParticipantColors(id);
	const borderColor = participantColor ?? colors.backgroundColor;

	const shouldShowClickToFollow =
		!isCurrentUser && isCanvasContentType(contentType) && participant.followCursor;

	const shouldShowClickToFind =
		Boolean(isScrollToFeatureEnabled) && participant.activity !== 'viewer';

	return (
		<Tooltip
			content={
				<>
					<TooltipText>{label}</TooltipText>
					{shouldShowClickToFollow && (
						<TooltipText>
							<FormattedMessage {...i18n.clickToFollow} />
						</TooltipText>
					)}
					{shouldShowClickToFind && (
						<Flex justifyContent="center">
							{Boolean(isEligibleToScroll) ? (
								<FormattedMessage {...i18n.clickToFind} />
							) : (
								<FormattedMessage {...i18n.clickToFindNotAvailable} />
							)}
						</Flex>
					)}
				</>
			}
		>
			<Flex direction="column" alignItems="center" gap="space.0" xcss={containerStyles}>
				<Avatar
					{...avatarProps}
					appearance="circle"
					borderColor={borderColor}
					label={label}
					name={name}
					size="small"
					src={displayedSrc}
				/>
				{activity === 'editor' && (
					<EditorAvatarBadge
						borderColor={borderColor}
						badgeTextColor={colors.textColor}
						name={name}
					/>
				)}
			</Flex>
		</Tooltip>
	);
};
